import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Job } from '../../../types/job';
import { useAdminStore } from '../../../stores/adminStore';
import { useJobStore } from '../../../stores/jobStore';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { toTorontoDateValue, torontoEndOfDayIso } from '../../jobs/jobCreation/jobCreationDates';

interface AdminPublishJobModalProps {
  job: Job;
  onClose: () => void;
  onSuccess?: () => void;
}

const TWO_WEEKS_DAYS = 14;

const padTwo = (value: number) => String(value).padStart(2, '0');

/**
 * Adds `days` to a `YYYY-MM-DD` date-only value using pure calendar-day
 * arithmetic (no timezone conversion), so it can't shift near DST boundaries.
 * Mirrors the helper in PublishJobModal.
 */
const addCalendarDays = (value: string, days: number): string => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${padTwo(date.getUTCMonth() + 1)}-${padTwo(date.getUTCDate())}`;
};

const getErrorMessage = (err: unknown): string => {
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(err);
};

const AdminPublishJobModal: React.FC<AdminPublishJobModalProps> = ({ job, onClose, onSuccess }) => {
  const { t } = useTranslation(['admin', 'jobs', 'common']);
  const { adminPublishJob } = useAdminStore();
  const { listingTypes, loadingListingTypes, fetchListingTypes } = useJobStore();

  const [listingTypeId, setListingTypeId] = useState<number | null>(job.listing_type?.id ?? null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Once true, blocks the load effect below from auto-retrying on its own so a
  // persistently failing fetch can't loop; the Retry button re-triggers the
  // fetch directly instead of resetting this flag.
  const [hasAttemptedListingTypesLoad, setHasAttemptedListingTypesLoad] = useState(false);

  const minExpiresAt = useMemo(() => toTorontoDateValue(new Date()), []);
  // A start_date that has already elapsed (e.g. a shadow job published long
  // after it was drafted) doesn't constrain expires_at — the "expiration
  // after start" check only guards against newly-created misconfigurations.
  const upcomingStartDate =
    job.start_date && job.start_date >= minExpiresAt ? job.start_date : null;
  const defaultExpiresAt = useMemo(() => {
    const twoWeeksOut = addCalendarDays(minExpiresAt, TWO_WEEKS_DAYS);
    return upcomingStartDate && upcomingStartDate < twoWeeksOut ? upcomingStartDate : twoWeeksOut;
  }, [minExpiresAt, upcomingStartDate]);
  const [expiresAt, setExpiresAt] = useState(defaultExpiresAt);
  const [selectionDate, setSelectionDate] = useState('');

  useEffect(() => {
    if (listingTypes.length > 0 || hasAttemptedListingTypesLoad) return;
    setHasAttemptedListingTypesLoad(true);
    void fetchListingTypes();
  }, [listingTypes.length, hasAttemptedListingTypesLoad, fetchListingTypes]);

  useEffect(() => {
    if (listingTypeId == null && listingTypes.length > 0) {
      const standard = listingTypes.find(lt => lt.code === 'standard');
      setListingTypeId(standard ? standard.id : listingTypes[0].id);
    }
  }, [listingTypeId, listingTypes]);

  // jobStore.fetchListingTypes catches its own errors and resolves rather than
  // rejecting, so a failed load can only be detected here as "we tried, we're
  // no longer loading, and the list is still empty" — otherwise this modal
  // gets stuck showing "Loading..." with Publish permanently disabled.
  const listingTypesLoadFailed =
    hasAttemptedListingTypesLoad && !loadingListingTypes && listingTypes.length === 0;

  const handleRetryListingTypes = () => {
    void fetchListingTypes();
  };

  // Publication deadlines are Toronto calendar days (matching the DB
  // constraint and PublishJobModal), so validation compares date-only strings
  // against Toronto's current date rather than UTC/browser-local instants.
  const isExpiresAtInPast = Boolean(expiresAt) && expiresAt < toTorontoDateValue(new Date());
  const isAfterStartDate = Boolean(upcomingStartDate) && expiresAt > upcomingStartDate!;
  const isExpiresAtValid = Boolean(expiresAt) && !isExpiresAtInPast && !isAfterStartDate;

  const minSelectionDate = useMemo(
    () => (expiresAt ? addCalendarDays(expiresAt, 1) : ''),
    [expiresAt]
  );
  const isSelectionDateBeforeExpiresAt = Boolean(selectionDate) && selectionDate <= expiresAt;
  const isSelectionDateAfterStartDate =
    Boolean(selectionDate) && Boolean(upcomingStartDate) && selectionDate > upcomingStartDate!;
  const isSelectionDateValid =
    !selectionDate || (!isSelectionDateBeforeExpiresAt && !isSelectionDateAfterStartDate);

  const canConfirm =
    listingTypeId != null && listingTypes.length > 0 && isExpiresAtValid && isSelectionDateValid;

  const handleConfirm = async () => {
    if (!canConfirm || listingTypeId == null) return;

    setSaving(true);
    setError(null);
    try {
      const refreshed = await adminPublishJob(
        job.id,
        listingTypeId,
        torontoEndOfDayIso(expiresAt),
        selectionDate ? torontoEndOfDayIso(selectionDate) : null
      );
      toast.success(t('jobModeration.publishSuccess', { ns: 'admin' }));
      if (!refreshed) {
        toast.error(t('jobModeration.publishRefreshError', { ns: 'admin' }));
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      const message = getErrorMessage(err);
      if (message.includes('JOB_NOT_SHADOW_JOB')) {
        setError(t('jobModeration.publishErrorNotShadowJob', { ns: 'admin' }));
      } else if (message.includes('JOB_NOT_PUBLISHABLE')) {
        setError(t('jobModeration.publishErrorNotPublishable', { ns: 'admin' }));
      } else if (message.includes('JOB_ALREADY_PUBLISHED')) {
        setError(t('jobModeration.publishErrorAlreadyPublished', { ns: 'admin' }));
      } else if (message.includes('Invalid listing type')) {
        setError(t('jobModeration.publishErrorNotPublishable', { ns: 'admin' }));
      } else if (message.includes('EXPIRES_AT_AFTER_START_DATE')) {
        setError(
          t('publish.expires_at_after_start_date_error', { ns: 'jobs', startDate: job.start_date })
        );
      } else if (message.includes('SELECTION_DATE_BEFORE_EXPIRES_AT')) {
        setError(t('publish.selection_date_before_expires_at_error', { ns: 'jobs' }));
      } else if (message.includes('SELECTION_DATE_AFTER_START_DATE')) {
        setError(
          t('publish.selection_date_after_start_date_error', {
            ns: 'jobs',
            startDate: job.start_date,
          })
        );
      } else {
        setError(t('error.generic', { ns: 'common' }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[36rem]">
      <h2 className="text-2xl font-bold text-secondary mb-2">
        {t('jobModeration.publishTitle', { ns: 'admin' })}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        {t('jobModeration.publishExplanation', { ns: 'admin' })}
      </p>

      <div className="mb-4">
        <label
          htmlFor="admin-publish-listing-type"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('jobModeration.publishListingTypeLabel', { ns: 'admin' })}
        </label>
        <select
          id="admin-publish-listing-type"
          value={listingTypeId ?? ''}
          onChange={e => setListingTypeId(e.target.value ? Number(e.target.value) : null)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={saving || !listingTypes.length}
        >
          {!listingTypes.length && (
            <option value="">
              {listingTypesLoadFailed
                ? t('jobModeration.publishListingTypesLoadError', { ns: 'admin' })
                : t('loading', { ns: 'common' })}
            </option>
          )}
          {listingTypes.map(lt => (
            <option key={lt.id} value={lt.id}>
              {t(`publish.priorities.${lt.code}.label`, { ns: 'jobs' })}
            </option>
          ))}
        </select>
        {listingTypesLoadFailed && (
          <div className="mt-2 flex items-center justify-between gap-3 text-sm text-red-600">
            <span>{t('jobModeration.publishListingTypesLoadError', { ns: 'admin' })}</span>
            <button
              type="button"
              onClick={handleRetryListingTypes}
              className="font-semibold underline hover:no-underline"
            >
              {t('jobModeration.publishRetry', { ns: 'admin' })}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label
          htmlFor="admin-publish-expires-at"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('jobModeration.publishExpiresAtLabel', { ns: 'admin' })}
        </label>
        <input
          id="admin-publish-expires-at"
          type="date"
          min={minExpiresAt}
          max={upcomingStartDate ?? undefined}
          value={expiresAt}
          onChange={e => setExpiresAt(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={saving}
        />
        {isAfterStartDate && (
          <p className="mt-1 text-sm text-red-600">
            {t('publish.expires_at_after_start_date_error', {
              ns: 'jobs',
              startDate: job.start_date,
            })}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label
          htmlFor="admin-publish-selection-date"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('publish.selection_date_title', { ns: 'jobs' })}
        </label>
        <input
          id="admin-publish-selection-date"
          type="date"
          min={minSelectionDate}
          max={upcomingStartDate ?? undefined}
          value={selectionDate}
          onChange={e => setSelectionDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={saving}
        />
        {isSelectionDateBeforeExpiresAt && (
          <p className="mt-1 text-sm text-red-600">
            {t('publish.selection_date_before_expires_at_error', { ns: 'jobs' })}
          </p>
        )}
        {!isSelectionDateBeforeExpiresAt && isSelectionDateAfterStartDate && (
          <p className="mt-1 text-sm text-red-600">
            {t('publish.selection_date_after_start_date_error', {
              ns: 'jobs',
              startDate: job.start_date,
            })}
          </p>
        )}
      </div>

      {error && <p className="mt-1 mb-4 text-sm text-red-600">{error}</p>}

      <p className="text-xs text-gray-500 mb-6">
        {t('jobModeration.publishNoCredits', { ns: 'admin' })}
      </p>

      <div className="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-end md:gap-4">
        <Button variant="simple-text" onClick={onClose} type="button" disabled={saving}>
          {t('form_actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          type="button"
          disabled={saving || !canConfirm}
        >
          {saving
            ? t('loading', { ns: 'common' })
            : t('jobModeration.confirmPublish', { ns: 'admin' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AdminPublishJobModal;
